import React from 'react';
import { Button, Card, Descriptions, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { formatSettlementAmountFromCents } from '../../../../utils/settlementAmountFormatter';
import { GET_DEPOSIT_INSURANCE_SOURCE_FILE_URL } from './graphql/queries';
import { DepositInsuranceContext } from './parseDepositInsuranceContext';

interface Props {
  context: DepositInsuranceContext | null;
  taskId?: string | number;
  formatTime: (time?: string | null) => string;
}

const money = (cents: number) => formatSettlementAmountFromCents(String(cents), 'USD');

const DepositInsuranceReviewSection: React.FC<Props> = ({ context, taskId, formatTime }) => {
  const [fetchUrl, { loading }] = useLazyQuery(GET_DEPOSIT_INSURANCE_SOURCE_FILE_URL, {
    fetchPolicy: 'network-only',
  });
  const effectiveTaskId = context?.taskId || taskId;

  const handleDownload = async () => {
    if (!effectiveTaskId) return;
    try {
      const result = await fetchUrl({ variables: { taskId: effectiveTaskId } });
      const url = result.data?.depositInsuranceSourceFileUrl;
      if (!url) {
        message.warning('源文件暂不可下载');
        return;
      }
      window.open(url, '_blank');
    } catch {
      message.error('获取下载链接失败');
    }
  };

  if (!context) {
    return <Card title="保证金/保险详情"><span className="text-gray-400">暂无任务数据</span></Card>;
  }

  return (
    <Card
      title="保证金/保险详情"
      extra={
        <Button icon={<DownloadOutlined />} loading={loading} onClick={() => void handleDownload()}>
          下载源文件
        </Button>
      }
    >
      <Descriptions bordered column={2}>
        <Descriptions.Item label="对应账期">
          {context.periodStart && context.periodEnd
            ? `${formatTime(context.periodStart)} ~ ${formatTime(context.periodEnd)}`
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="费用类型分布">
          {`Deposit ${context.depositCount} 笔 / Insurance ${context.insuranceCount} 笔`}
        </Descriptions.Item>
        <Descriptions.Item label="总笔数">{context.validCount}</Descriptions.Item>
        <Descriptions.Item label="总金额">{money(context.totalAmountCents)}</Descriptions.Item>
        <Descriptions.Item label="Deposit 小计">{money(context.depositAmountCents)}</Descriptions.Item>
        <Descriptions.Item label="Insurance 小计">{money(context.insuranceAmountCents)}</Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default DepositInsuranceReviewSection;
